import { useEffect, useState } from 'react';
import {
  getCurrentUser,
  signInWithRedirect,
  signOut,
  signIn,
  signUp,
  confirmSignIn as amplifyConfirmSignIn,
  fetchAuthSession
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { api } from '../api/client';
import { isMockAuth, useMockAuth } from '../test-utils/mock-auth';

// Returned by sendCode when the identifier isn't in Cognito but the
// pre-signIn lookup tells us the same email/phone already exists under
// a different auth method. The caller renders a "use {method} instead"
// prompt and skips the signUp fallback — preventing a duplicate account.
export interface ExistingAccountHint {
  existingAccount: true;
  methods: ('passwordless' | 'google' | 'github')[];
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  userId: string | null;
  email: string | null;
  phoneNumber: string | null;
}

// Backend tag thrown by BWS-Cognito_post_authentication.mjs when a
// federated identity has just been merged into an existing native
// account. The frontend auto-retries signInWithRedirect once when it
// sees this tag (Google's session cookie completes the second
// attempt instantly, with the native sub).
const RELINK_REQUIRED_TAG = 'AccountLinked_RetryRequired';
const RELINK_RETRY_FLAG = 'oauth_relink_retry';
const RELINK_PROVIDER_FLAG = 'oauth_relink_provider';

function useRealAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    userId: null,
    email: null,
    phoneNumber: null,
  });

  const checkAuth = async () => {
    try {
      // Amplify v6's TokenOrchestrator.waitForInflightOAuth blocks both
      // getCurrentUser and fetchAuthSession until any in-flight OAuth
      // code exchange has finished, so the call order doesn't gate
      // OAuth processing — just authentication state.
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        throw new Error('not_signed_in');
      }
      const claims = session.tokens.idToken.payload;
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        userId: user.userId,
        email: (claims?.email as string) ?? null,
        phoneNumber: (claims?.phone_number as string) ?? null,
      });
    } catch {
      // Dead-callback detection. Amplify v6's `attemptCompleteOAuthFlow`
      // runs as a side effect of `Amplify.configure()` — i.e. before
      // i18nReady resolves and React mounts. On the FAILURE path
      // Amplify only dispatches `signInWithRedirect_failure`; it does
      // NOT clear `?code=` from the URL (only the success path calls
      // `clearHistory`). If the exchange failed during that pre-mount
      // window, the Hub event fires into the void and we'd otherwise
      // sit on /?code=… forever. Bounce immediately.
      //
      // Skipped while a relink retry is in flight (sessionStorage
      // RELINK_RETRY_FLAG) — the Hub handler will drive that path.
      if (
        typeof window !== 'undefined' &&
        window.location.search.includes('code=') &&
        sessionStorage.getItem(RELINK_RETRY_FLAG) !== '1'
      ) {
        window.history.replaceState({}, '', '/');
        window.location.replace('/signin?oauth=failed');
        return;
      }
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        userId: null,
        email: null,
        phoneNumber: null,
      });
    }
  };

  useEffect(() => {
    void checkAuth();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect_failure': {
          // Backend signal: federated user was just merged into an
          // existing native account. One automatic retry through the
          // provider's session cookie completes the sign-in with the
          // native sub. See BWS-Cognito_post_authentication.mjs.
          const errorMsg =
            payload.data &&
            typeof payload.data === 'object' &&
            'error' in payload.data &&
            payload.data.error instanceof Error
              ? payload.data.error.message ?? ''
              : '';

          if (errorMsg.includes(RELINK_REQUIRED_TAG)) {
            if (
              typeof sessionStorage !== 'undefined' &&
              sessionStorage.getItem(RELINK_RETRY_FLAG) !== '1'
            ) {
              sessionStorage.setItem(RELINK_RETRY_FLAG, '1');
              const provider =
                sessionStorage.getItem(RELINK_PROVIDER_FLAG) ?? 'Google';
              if (typeof window !== 'undefined') {
                window.history.replaceState({}, '', '/');
              }
              void signInWithRedirect(
                provider === 'GitHub'
                  ? { provider: { custom: 'GitHub' } }
                  : { provider: 'Google' }
              );
              return;
            }
            // Already retried once and still failing — give up,
            // surface a clear hint to click again manually.
            sessionStorage.removeItem(RELINK_RETRY_FLAG);
            if (typeof window !== 'undefined') {
              window.history.replaceState({}, '', '/');
              window.location.replace('/signin?oauth=relink-success');
            }
            return;
          }

          // Generic OAuth failure (expired/invalid state, network…).
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            userId: null,
            email: null,
            phoneNumber: null,
          });
          if (typeof window !== 'undefined') {
            window.history.replaceState({}, '', '/');
            if (window.location.pathname === '/') {
              window.location.replace('/signin?oauth=failed');
            }
          }
          break;
        }
        case 'signedIn':
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(RELINK_RETRY_FLAG);
            sessionStorage.removeItem(RELINK_PROVIDER_FLAG);
          }
          if (
            typeof window !== 'undefined' &&
            window.location.search.includes('code=')
          ) {
            window.history.replaceState({}, '', window.location.pathname);
          }
          void checkAuth();
          break;
        case 'signedOut':
          void checkAuth();
          break;
      }
    });

    return unsubscribe;
  }, []);

  // Passwordless auth. sendCode kicks off a Cognito Custom Auth flow —
  // the CreateAuthChallenge Lambda generates a 6-digit OTP and dispatches
  // it via WhatsApp (phone_number identifier) or SES (email identifier).
  // confirmCode submits the code; on success Cognito issues tokens AND
  // the VerifyAuthChallengeResponse Lambda auto-creates a verified
  // primary Channel row — so one OTP covers auth + channel setup.
  //
  // clientMetadata.turnstileToken (optional) rides along so the
  // backend can verify a Cloudflare Turnstile challenge. Unconfigured
  // installs simply ignore it.
  const sendCode = async (
    identifier: string,
    clientMetadata?: Record<string, string>,
    // Email collected alongside a PHONE identifier at signup. Stored as a
    // Cognito attribute so the account always has a second reachable
    // destination — Meta's #131049 cap silently drops WhatsApp alerts for
    // heavy users, and a phone-only account has nowhere to fall back to.
    // It is NOT proven by this flow's OTP (which goes to the phone), so
    // the backend records it pending_verification until its own OTP.
    extraEmail?: string,
  ): Promise<
    | { destination?: string; channel?: 'whatsapp' | 'email' }
    | ExistingAccountHint
  > => {
    const username = normaliseIdentifier(identifier);
    const isEmail = username.includes('@');
    try {
      const r = await signIn({ username, options: { authFlowType: 'CUSTOM_WITHOUT_SRP', clientMetadata } });
      return readNextStep(r);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'UserNotFoundException') {
        // Fragmentation guard: before we fall back to signUp (which
        // would create a brand-new Cognito account), ask the backend
        // whether the same email/phone already exists under a
        // different auth method. If Google or GitHub picked it up
        // first, we return the hint so the caller can steer the user
        // to that provider's button instead of silently producing a
        // second account the user can't reconcile.
        try {
          const lookup = await api.identifierLookup(username);
          if (lookup.exists && lookup.methods.some((m) => m !== 'passwordless')) {
            return {
              existingAccount: true,
              methods: lookup.methods,
            };
          }
        } catch {
          // Lookup failure is non-fatal — fall through to the old
          // signUp behaviour so a transient /auth/identifier-lookup
          // outage can't lock new users out.
        }

        // NOTE: no autoSignIn here. PreSignUp auto-confirms the user, and
        // Amplify would then silently fire a CUSTOM_AUTH flow — stacking
        // a second CreateAuthChallenge invocation on top of the explicit
        // signIn below. The second hit the per-destination cooldown and
        // surfaced "Too many requests. Try again in ~300s" even though the
        // first OTP was delivered successfully.
        await signUp({
          username,
          password: randomPassword(),
          options: {
            userAttributes: isEmail
              ? { email: username }
              : {
                  phone_number: username,
                  ...(extraEmail ? { email: extraEmail.trim().toLowerCase() } : {}),
                },
            clientMetadata,
          },
        });
        const r = await signIn({ username, options: { authFlowType: 'CUSTOM_WITHOUT_SRP', clientMetadata } });
        return readNextStep(r);
      }
      throw err;
    }
  };

  const confirmCode = async (code: string) => {
    const result = await amplifyConfirmSignIn({ challengeResponse: code.trim() });
    if (result.nextStep?.signInStep === 'DONE') {
      await checkAuth();
      return result;
    }
    // Wrong code path. The DefineAuthChallenge trigger allows up to 3
    // attempts before failing the auth — until then Cognito issues a
    // fresh CUSTOM_CHALLENGE and Amplify reports
    // `CONTINUE_SIGN_IN_WITH_CUSTOM_CHALLENGE`. That is NOT a thrown
    // error, so without this branch the AuthPage's catch never fires
    // and the user sees no feedback. Throw a tagged error so the
    // caller can show "Incorrect code" rather than failing silently.
    const err = new Error('Incorrect code. Please check the latest one and try again.');
    err.name = 'IncorrectCodeError';
    throw err;
  };

  const signInWithGoogle = () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(RELINK_PROVIDER_FLAG, 'Google');
      sessionStorage.removeItem(RELINK_RETRY_FLAG);
    }
    void signInWithRedirect({ provider: 'Google' });
  };

  const signInWithGitHub = () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(RELINK_PROVIDER_FLAG, 'GitHub');
      sessionStorage.removeItem(RELINK_RETRY_FLAG);
    }
    void signInWithRedirect({ provider: { custom: 'GitHub' } });
  };

  return {
    ...authState,
    signIn: signInWithGoogle,
    signOut: async () => { await signOut(); },
    sendCode,
    confirmCode,
    signInWithGoogle,
    signInWithGitHub,
  };
}

// E.164-normalise a phone or lowercase/trim an email. Phone rules are
// intentionally permissive — Cognito will reject malformed values itself.
function normaliseIdentifier(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes('@')) return trimmed.toLowerCase();
  // phone: strip spaces/dashes/parentheses, keep leading +
  return '+' + trimmed.replace(/[^\d]/g, '').replace(/^\+?/, '');
}

function randomPassword(): string {
  // Cognito's policy requires min 8 chars. We never use this password —
  // the OTP flow is the only credential path enabled on the client.
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return 'A1!' + Array.from(arr, b => b.toString(36)).join('').slice(0, 21);
}

interface SignInResultWithChallenge {
  nextStep?: {
    signInStep?: string;
    additionalInfo?: Record<string, string>;
  };
}

function readNextStep(r: SignInResultWithChallenge) {
  const info = r.nextStep?.additionalInfo;
  return {
    destination: info?.destination,
    channel: info?.channel as 'whatsapp' | 'email' | undefined,
  };
}

// Select implementation at module load time (safe for hooks — always the same function)
const useAuthImpl = isMockAuth() ? useMockAuth : useRealAuth;

export function useAuth() {
  return useAuthImpl();
}
